/**
 * @fileoverview Modal component for creating and editing individual questions in Adivina Adivinador (Riddle).
 * Allows operator to edit the question instruction/phrase, upload silhouette image with preview,
 * upload revealed response image, specify response text, and configure the 4 response options with
 * correct answer selection.
 * 
 * @module components/Admin/Games/Riddle/RiddleQuestionFormModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { saveQuestion, uploadMedia } from './../../../../utilities/BL/Admin/Games/Riddle';

/**
 * Resolves a media path into an absolute preview URL.
 * 
 * @param {string} mediaPath - Relative or absolute image path.
 * @returns {string} Absolute preview URL.
 */
const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
    return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * Reusable Drag & Drop Image Uploader field with preview and remove button.
 */
const ImageDropzone = ({
    fieldLabel,
    inputIdentifier,
    currentValue,
    onFileSelected,
    onClearFile,
    isUploading,
    helpText
}) => {
    const [isDragHover, setIsDragHover] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragHover(true);
    };

    const handleDragLeave = () => {
        setIsDragHover(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragHover(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            onFileSelected(e.dataTransfer.files[0]);
        }
    };

    return (
        <div className="mb-3">
            <label className="form-label fw-bold small text-secondary mb-1">
                {fieldLabel}
            </label>
            <div
                className={`border border-2 border-dashed rounded-3 p-3 text-center transition ${
                    isDragHover ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle bg-light'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(inputIdentifier).click()}
                style={{ cursor: 'pointer', minHeight: '80px' }}
            >
                {isUploading ? (
                    <div className="py-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span className="small text-primary fw-bold">Optimizando y subiendo imagen...</span>
                    </div>
                ) : (
                    <div>
                        <i className="bi bi-cloud-arrow-up text-primary fs-4 d-block mb-1"></i>
                        <span className="small text-muted">
                            Arrastra aquí la imagen o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                        {helpText && <small className="text-secondary d-block mt-1">{helpText}</small>}
                    </div>
                )}
                <input
                    id={inputIdentifier}
                    type="file"
                    className="d-none"
                    accept="image/*"
                    onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                            onFileSelected(e.target.files[0]);
                        }
                    }}
                />
            </div>

            {currentValue && (
                <div className="mt-2 text-center bg-white p-2 rounded-3 border shadow-sm position-relative d-inline-block">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-1 rounded-circle p-0"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClearFile();
                        }}
                        title="Eliminar imagen"
                        style={{ width: '22px', height: '22px', lineHeight: '18px' }}
                    >
                        <i className="bi bi-x"></i>
                    </button>
                    <img
                        src={resolveMediaUrl(currentValue)}
                        alt="Vista previa"
                        style={{ maxHeight: '90px', maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Riddle Question Form Modal Component.
 * 
 * @function RiddleQuestionFormModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {Function} props.onClose - Modal close handler.
 * @param {Object|null} props.questionToEdit - Question data to edit or null for new question.
 * @param {string} props.triviaSetId - Associated trivia set GUID.
 * @param {Function} props.onQuestionSaved - Callback after successful question save.
 * @returns {React.ReactElement|null}
 */
export const RiddleQuestionFormModal = ({
    isOpen,
    onClose,
    questionToEdit = null,
    triviaSetId,
    onQuestionSaved
}) => {
    const { showMessage } = useFloatMessage();

    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingSilhouette, setIsUploadingSilhouette] = useState(false);
    const [isUploadingResponseImg, setIsUploadingResponseImg] = useState(false);
    const [validationError, setValidationError] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        id: undefined,
        triviaSetId: triviaSetId,
        instruction: '¿Quién es este personaje?',
        imageUrl: '',
        responseImageUrl: '',
        responseText: '',
        orderIndex: 0,
        isActive: true,
        options: [
            { text: '', isCorrect: true, orderIndex: 0 },
            { text: '', isCorrect: false, orderIndex: 1 }
        ]
    });

    // Initialize form when questionToEdit or triviaSetId changes
    useEffect(() => {
        if (questionToEdit) {
            const rawOptions = questionToEdit.options || [];
            const formattedOptions = rawOptions.length >= 2
                ? rawOptions.map((opt, idx) => ({
                    id: opt?.id || undefined,
                    text: opt?.text || '',
                    isCorrect: opt?.isCorrect || false,
                    imageUrl: opt?.imageUrl || null,
                    orderIndex: idx
                }))
                : (rawOptions.length === 1
                    ? [
                        {
                            id: rawOptions[0]?.id || undefined,
                            text: rawOptions[0]?.text || '',
                            isCorrect: true,
                            imageUrl: rawOptions[0]?.imageUrl || null,
                            orderIndex: 0
                        },
                        { text: '', isCorrect: false, orderIndex: 1 }
                    ]
                    : [
                        { text: '', isCorrect: true, orderIndex: 0 },
                        { text: '', isCorrect: false, orderIndex: 1 }
                    ]);

            // Ensure at least one option is marked correct
            if (!formattedOptions.some(opt => opt.isCorrect)) {
                formattedOptions[0].isCorrect = true;
            }

            setFormData({
                id: questionToEdit.id || undefined,
                triviaSetId: questionToEdit.triviaSetId || triviaSetId,
                instruction: questionToEdit.instruction || '',
                imageUrl: questionToEdit.imageUrl || '',
                responseImageUrl: questionToEdit.responseImageUrl || '',
                responseText: questionToEdit.responseText || '',
                orderIndex: questionToEdit.orderIndex || 0,
                isActive: questionToEdit.isActive !== false,
                options: formattedOptions
            });
        } else {
            setFormData({
                id: undefined,
                triviaSetId: triviaSetId,
                instruction: '¿Quién es este personaje?',
                imageUrl: '',
                responseImageUrl: '',
                responseText: '',
                orderIndex: 0,
                isActive: true,
                options: [
                    { text: '', isCorrect: true, orderIndex: 0 },
                    { text: '', isCorrect: false, orderIndex: 1 }
                ]
            });
        }
        setValidationError(null);
    }, [questionToEdit, triviaSetId, isOpen]);

    // Handle Escape Key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    /**
     * Updates an option's text value.
     */
    const handleOptionTextChange = (index, newText) => {
        setFormData((prev) => {
            const nextOptions = [...prev.options];
            nextOptions[index] = { ...nextOptions[index], text: newText };
            return { ...prev, options: nextOptions };
        });
    };

    /**
     * Marks a specific option as the correct one.
     */
    const handleCorrectOptionSelect = (index) => {
        setFormData((prev) => {
            const nextOptions = prev.options.map((opt, idx) => ({
                ...opt,
                isCorrect: idx === index
            }));
            return { ...prev, options: nextOptions };
        });
    };

    /**
     * Adds a new response option.
     */
    const handleAddOption = () => {
        setFormData((prev) => {
            const nextOptions = [
                ...prev.options,
                { text: '', isCorrect: false, orderIndex: prev.options.length }
            ];
            return { ...prev, options: nextOptions };
        });
    };

    /**
     * Removes an option at a given index if there are at least 2 remaining.
     */
    const handleRemoveOption = (index) => {
        setFormData((prev) => {
            if (prev.options.length <= 2) return prev;
            const filtered = prev.options.filter((_, idx) => idx !== index);
            // Re-assign orderIndex
            const reindexed = filtered.map((opt, idx) => ({
                ...opt,
                orderIndex: idx
            }));
            // If the deleted option was correct, make the first one correct
            if (!reindexed.some(opt => opt.isCorrect)) {
                reindexed[0].isCorrect = true;
            }
            return { ...prev, options: reindexed };
        });
    };

    /**
     * Uploads silhouette / main question image.
     */
    const handleUploadSilhouette = async (file) => {
        setIsUploadingSilhouette(true);
        try {
            const result = await uploadMedia(file, 'silhouette', triviaSetId);
            if (result.success && result.url) {
                setFormData((prev) => ({ ...prev, imageUrl: result.url }));
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Silueta optimizada y subida correctamente.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: result.message || 'Error al subir la silueta.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al subir la silueta.'
            });
        } finally {
            setIsUploadingSilhouette(false);
        }
    };

    /**
     * Uploads response revealed image.
     */
    const handleUploadResponseImg = async (file) => {
        setIsUploadingResponseImg(true);
        try {
            const result = await uploadMedia(file, 'response', triviaSetId);
            if (result.success && result.url) {
                setFormData((prev) => ({ ...prev, responseImageUrl: result.url }));
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Imagen de respuesta subida correctamente.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: result.message || 'Error al subir la imagen de respuesta.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al subir imagen de respuesta.'
            });
        } finally {
            setIsUploadingResponseImg(false);
        }
    };

    /**
     * Validates and submits the question form.
     */
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setValidationError(null);

        // Validations
        if (!formData.instruction.trim()) {
            setValidationError('La frase o instrucción es requerida.');
            return;
        }

        if (!formData.imageUrl.trim()) {
            setValidationError('La imagen de la silueta u objeto a adivinar es requerida.');
            return;
        }

        if (!formData.options || formData.options.length < 2) {
            setValidationError('Debes ingresar al menos 2 opciones de respuesta.');
            return;
        }

        for (let i = 0; i < formData.options.length; i++) {
            if (!formData.options[i].text.trim()) {
                setValidationError(`La opción ${String.fromCharCode(65 + i)} no puede estar vacía.`);
                return;
            }
        }

        if (!formData.options.some(opt => opt.isCorrect)) {
            setValidationError('Debes marcar una opción como la respuesta correcta.');
            return;
        }

        setIsSaving(true);
        try {
            const saveResult = await saveQuestion(formData);
            if (saveResult.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: questionToEdit ? 'Pregunta actualizada exitosamente.' : 'Pregunta agregada exitosamente.'
                });
                if (onQuestionSaved) onQuestionSaved(saveResult.data);
                onClose();
            } else {
                setValidationError(saveResult.message || 'Error al guardar la pregunta.');
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: saveResult.message || 'Error al guardar la pregunta.'
                });
            }
        } catch (error) {
            setValidationError('Error inesperado al procesar la solicitud.');
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al procesar la solicitud.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className="cModal__Background" onClick={onClose}></div>
            <div className="cModal__Container" style={{ maxWidth: '680px' }}>
                <div className="cModal__Container__Content p-4 text-start">
                    <button
                        type="button"
                        className="Btn__Adsl--RedOutline cModal__Container__Content__CloseButton"
                        onClick={onClose}
                    >
                        X
                    </button>

                    <h4 className="fw-bold mb-3 text-dark d-flex align-items-center">
                        <i className="bi bi-question-circle text-primary me-2"></i>
                        {questionToEdit ? 'Editar Adivinanza' : 'Nueva Adivinanza'}
                    </h4>

                    {validationError && (
                        <div className="alert alert-danger py-2 small mb-3">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            {validationError}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Instruction / Phrase */}
                        <div className="mb-3">
                            <label className="form-label fw-bold small text-secondary mb-1">
                                Frase o Instrucción:
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ej: ¿Quién es este personaje?"
                                value={formData.instruction}
                                onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
                                maxLength={500}
                                required
                            />
                        </div>

                        {/* Silhouette / Main Image & Revealed Image (Side by Side) */}
                        <div className="row">
                            <div className="col-md-6">
                                <ImageDropzone
                                    fieldLabel="1. Imagen Silueta (Oculta): *"
                                    inputIdentifier="riddle-silhouette-input"
                                    currentValue={formData.imageUrl}
                                    onFileSelected={handleUploadSilhouette}
                                    onClearFile={() => setFormData({ ...formData, imageUrl: '' })}
                                    isUploading={isUploadingSilhouette}
                                    helpText="Recomendado: PNG con fondo transparente"
                                />
                            </div>
                            <div className="col-md-6">
                                <ImageDropzone
                                    fieldLabel="2. Imagen Respuesta (Revelada):"
                                    inputIdentifier="riddle-response-img-input"
                                    currentValue={formData.responseImageUrl}
                                    onFileSelected={handleUploadResponseImg}
                                    onClearFile={() => setFormData({ ...formData, responseImageUrl: '' })}
                                    isUploading={isUploadingResponseImg}
                                    helpText="Imagen a todo color del personaje o respuesta"
                                />
                            </div>
                        </div>

                        {/* Revealed Response Text */}
                        <div className="mb-3">
                            <label className="form-label fw-bold small text-secondary mb-1">
                                Texto de Respuesta Revelada (Opcional):
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ej: ¡Es Tunita!"
                                value={formData.responseText}
                                onChange={(e) => setFormData({ ...formData, responseText: e.target.value })}
                                maxLength={500}
                            />
                        </div>

                        {/* Options Section */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label fw-bold small text-secondary mb-0">
                                    Opciones de Respuesta (mínimo 2 alternativas):
                                </label>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary fw-semibold"
                                    onClick={handleAddOption}
                                    disabled={formData.options.length >= 8}
                                >
                                    <i className="bi bi-plus-circle me-1"></i>
                                    Agregar opción
                                </button>
                            </div>
                            <div className="bg-light p-3 rounded-3 border">
                                {formData.options.map((opt, idx) => (
                                    <div key={idx} className="input-group mb-2">
                                        <span className="input-group-text bg-white fw-bold text-secondary" style={{ width: '42px', justifyContent: 'center' }}>
                                            {String.fromCharCode(65 + idx)}
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={`Texto opción ${String.fromCharCode(65 + idx)}`}
                                            value={opt.text}
                                            onChange={(e) => handleOptionTextChange(idx, e.target.value)}
                                            maxLength={300}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${opt.isCorrect ? 'btn-success' : 'btn-outline-secondary'}`}
                                            onClick={() => handleCorrectOptionSelect(idx)}
                                            title="Marcar como respuesta correcta"
                                            style={{ minWidth: '120px' }}
                                        >
                                            {opt.isCorrect ? (
                                                <><i className="bi bi-check-circle-fill me-1"></i> Correcta</>
                                            ) : (
                                                <><i className="bi bi-circle me-1"></i> Seleccionar</>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleRemoveOption(idx)}
                                            disabled={formData.options.length <= 2}
                                            title={formData.options.length <= 2 ? 'Se requiere un mínimo de 2 opciones' : 'Eliminar esta opción'}
                                            style={{ width: '42px' }}
                                        >
                                            <i className="bi bi-trash-fill"></i>
                                        </button>
                                    </div>
                                ))}
                                <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                                    <small className="text-muted">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Haz clic en <strong>"Seleccionar"</strong> para marcar la respuesta correcta.
                                    </small>
                                    <small className="text-secondary fw-semibold">
                                        Total: {formData.options.length} opciones
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Order & Active Status */}
                        <div className="row mb-3 align-items-center">
                            <div className="col-6">
                                <label className="form-label fw-bold small text-secondary mb-1">
                                    Orden:
                                </label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={formData.orderIndex}
                                    onChange={(e) => setFormData({ ...formData, orderIndex: Number(e.target.value) })}
                                    min={0}
                                />
                            </div>
                            <div className="col-6 pt-3">
                                <div className="form-check form-switch">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="questionIsActiveSwitch"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                    <label className="form-check-label fw-bold small text-dark" htmlFor="questionIsActiveSwitch">
                                        Pregunta activa
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                            <button
                                type="button"
                                className="btn btn-secondary px-4"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary px-4 fw-bold"
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2-circle me-1"></i>
                                        Guardar Adivinanza
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};
